"use client"

import { PermissionContext } from "@tc/ui-permission/hooks/PermissionProvider"
import { useSession } from "next-auth/react"
import { createContext, useContext, useEffect, useMemo, useRef } from "react"
import {
  getUserScopedStorageKey,
  TYPESENSE_APIKEY_IN_STORAGE,
  TYPESENSE_PARTY_APIKEY_IN_STORAGE,
  useTypesenseApiKey,
} from "./useUiTypesenseClient"

export interface TypesenseProviderProps {
  typesenseHost: string
  typesenseApiKeyMutation: string | object
  typesenseMultiCollectionApiKeyMutation?: string | object
  typesensePartyApiKeyMutation?: string | object
  userId?: string
  isSuspended?: boolean
  children: React.ReactNode
}

export interface TypesenseContextProps {
  typesenseHost: string
  typesenseApiKeyMutation: string
  typesenseMultiCollectionApiKeyMutation: string
  apiKey?: string
  refreshKeys?: () => void
  isRefreshing?: boolean
  hasMultiKeyError?: boolean
  multiKeyStorageKey?: string
  multiCollectionKeysV2?: {
    exports?: {
      exportCertificates?: string
      transferRequests?: string
      sourceTransfers?: string
      registration?: string
      registrationAll?: string
      supportingDocuments?: string
    }
    transfers?: {
      transferRequests?: string
      sourceTransfers?: string
      registration?: string
      registrationAll?: string
      supportingDocuments?: string
    }
    supportingDocs?: {
      registration?: string
      registrationAll?: string
      supportingDocuments?: string
    }
    templates?: string
    products?: string
    exporters?: string
    agency?: string
    expires?: string
    partyApiKey?: string
    ocaExports?: string
    ocaUsers?: string
  }
}

export const TypesenseContext = createContext<TypesenseContextProps>({
  typesenseHost: "",
  typesenseApiKeyMutation: "",
  typesenseMultiCollectionApiKeyMutation: "",
})

export const TypesenseProvider = (props: TypesenseProviderProps) => {
  const mutationToUse =
    props.typesenseMultiCollectionApiKeyMutation ||
    props.typesensePartyApiKeyMutation

  if (!props.typesenseApiKeyMutation || !mutationToUse) {
    console.warn(
      "for typesense, recommend to config its typesenseApiKeyMutation and typesenseMultiCollectionApiKeyMutation",
    )
  }

  // Get auth token expiry from session to tie Typesense key lifecycle to auth token
  const { data: session } = useSession()
  const authTokenExpiresAt = (session as any)?.token?.expires_at as
    | number
    | undefined

  // Scope localStorage keys by userId to prevent cross-session key leakage
  const singleKeyStorageKey = getUserScopedStorageKey(
    TYPESENSE_APIKEY_IN_STORAGE,
    props.userId,
  )
  const multiKeyStorageKey = getUserScopedStorageKey(
    TYPESENSE_PARTY_APIKEY_IN_STORAGE,
    props.userId,
  )

  const {
    apiKey,
    refreshKeys: refreshSingleKey,
    isRefreshing: isRefreshingSingle,
  } = useTypesenseApiKey({
    mutation:
      typeof props.typesenseApiKeyMutation === "string"
        ? props.typesenseApiKeyMutation
        : props.typesenseApiKeyMutation.toString(),
    localStorageKey: singleKeyStorageKey,
    authTokenExpiresAt,
    pause: props.isSuspended,
  })

  const {
    apiKey: multiCollectionKeysRaw,
    refreshKeys: refreshMultiKeys,
    isRefreshing: isRefreshingMulti,
    hasError: hasMultiKeyError,
  } = useTypesenseApiKey({
    mutation: mutationToUse
      ? typeof mutationToUse === "string"
        ? mutationToUse
        : mutationToUse.toString()
      : "",
    localStorageKey: multiKeyStorageKey,
    authTokenExpiresAt,
    pause: props.isSuspended,
  })

  const refreshAllKeys = () => {
    if (hasMultiKeyError) return
    refreshSingleKey()
    refreshMultiKeys()
  }

  // Force refresh keys when permissions/feature flags change
  const { permissions } = useContext(PermissionContext)
  const permissionsKey = JSON.stringify(permissions ?? [])
  const prevPermissionsKeyRef = useRef(permissionsKey)
  useEffect(() => {
    if (prevPermissionsKeyRef.current !== permissionsKey) {
      prevPermissionsKeyRef.current = permissionsKey
      refreshAllKeys()
    }
  }, [permissionsKey])

  // Parse multi-collection keys if available and it's a JSON string
  const multiCollectionKeys = useMemo<
    TypesenseContextProps["multiCollectionKeysV2"]
  >(() => {
    if (multiCollectionKeysRaw && typeof multiCollectionKeysRaw === "string") {
      try {
        return JSON.parse(multiCollectionKeysRaw)
      } catch {
        // If parsing fails, it's a single key, not multi-collection
        return undefined
      }
    }
    if (multiCollectionKeysRaw && typeof multiCollectionKeysRaw === "object") {
      return multiCollectionKeysRaw
    }
    return undefined
  }, [multiCollectionKeysRaw])

  return (
    <TypesenseContext.Provider
      value={{
        typesenseHost: props.typesenseHost,
        typesenseApiKeyMutation:
          typeof props.typesenseApiKeyMutation === "string"
            ? props.typesenseApiKeyMutation
            : JSON.stringify(props.typesenseApiKeyMutation),
        typesenseMultiCollectionApiKeyMutation:
          typeof props.typesenseMultiCollectionApiKeyMutation === "string"
            ? props.typesenseMultiCollectionApiKeyMutation
            : JSON.stringify(
                props.typesenseMultiCollectionApiKeyMutation || "",
              ),
        apiKey: apiKey,
        refreshKeys: refreshAllKeys,
        isRefreshing: isRefreshingSingle || isRefreshingMulti,
        hasMultiKeyError: hasMultiKeyError,
        multiKeyStorageKey: multiKeyStorageKey,
        multiCollectionKeysV2: multiCollectionKeys,
      }}
    >
      {props.children}
    </TypesenseContext.Provider>
  )
}
